<template>
        <div class="d-flex justify-content-center spinner-border text-primary spinner-position form" v-if="page.isLoading"></div>
        <div class="spinner" v-if='page.isLoading'></div>
        <div class="container">
            <o-bread-crumb>
                <template v-slot:pageName>
                    Product
                </template>
                <template v-slot:pageDetail>
                    {{ page.cardTitle }}
                </template>
            </o-bread-crumb>
            <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
            </div>

            <!-- Feature Disabled State -->
            <div v-if="!isMerchantProductEnabled" class="feature-disabled-container mt-4">
                <div class="disabled-content">
                    <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                    <h4>Product Management Disabled</h4>
                    <p>The Product Management feature has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
                </div>
            </div>

            <div v-if="isMerchantProductEnabled" class="d-flex justify-content-start">
                <o-card :card-title="page.cardTitle" class="custom-table mw-700">
                    <o-form @submit.prevent="change()" autocomplete="off">
                        <o-form autocomplete="off">
                            <div class="d-flex justify-content-between me-2">
                                <div class="col-md-6 me-2">
                                    <o-form-group title="name" name="name">
                                        <span class="text-danger"> *</span>
                                        <o-input-text name="name" v-model="product.name" type="text" :required="true" :autocomplete="'off'"/>
                                            <div v-if="validation.name">
                                                <div class="alert alert-danger mt-1" role="alert">
                                                    {{ validation.name[0] }}
                                                </div>
                                            </div>
                                    </o-form-group>
                                </div>
                                <div class="col-md-6">
                                    <o-form-group title="price" name="price">
                                        <span class="text-danger"> *</span>
                                        <o-input-text name="price" :autocomplete="'off'" v-model="product.price" type="text" mask="idr" :required="true"/>
                                        <div v-if="validation.price">
                                            <div class="alert alert-danger mt-1" role="alert">
                                                {{ validation.price[0] }}
                                            </div>
                                        </div>
                                    </o-form-group>
                                </div>
                            </div>
                            <o-form-group title="merchant id" name="merchant_id" v-if="selectedBusinessProfile.merchant_id == ''">
                                <o-select v-model="product.merchant_id" :options="merchants" autocomplete="'off'"></o-select>
                                <div v-if="validation.merchant_id">
                                    <div class="alert alert-danger mt-1" role="alert">
                                        {{ validation.merchant_id[0] }}
                                    </div>
                                </div>
                            </o-form-group>
                        </o-form>
                        <div class="button-container mt-2">
                            <o-button type="submit" :size="'sm'" :variant="'primary'" :white="'white'">
                              Save
                            </o-button>
                            <o-button @click="cancel()" :size="'sm'" :variant="'outline-secondary'" :white="'white'">
                                Cancel
                            </o-button>
                        </div>
                    </o-form>
                </o-card>
            </div>
        </div>
</template>

<script>
import { OCard, OForm, OFormGroup, OInputText, OButton, OSelect, OBreadCrumb } from '@/components/OmnihControl';
import { reactive, ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import store from '@/store'

export default {
    name: 'Addproduct',
    components: {
        OCard,
        OForm,
        OFormGroup,
        OInputText,
        OButton,
        OSelect,
        OBreadCrumb
    },
    setup(){

        const vuexStore = useStore();

        const isMerchantProductEnabled = computed(() => {
            const flag = vuexStore.getters['FeatureFlag/getMerchantProductFlag'];
            return flag !== 'NONACTIVE';
        });

        const page = reactive({
            isLoading: false,
            cardTitle: 'Create Product'
        });

        const product = reactive({
            merchant_id: '',
            name: '',
            price: '',
        });

        const selectedBusinessProfile = reactive({
            branch_id: '',
            merchant_id: ''
        });

        const options = ref([]);

        const validation = ref([]);
        const error = ref('');
        
        const router = useRouter();

        const route = useRoute();

        const merchants = ref([]);

        onMounted(() => {
            loadSelectedBusinessProfile();

            if (route.params.id) {
                loadState(route.params.id);
            }
        });

        async function loadState(id) {
            page.isLoading = true;

            try {
                const data = await store.dispatch('Product/read_single', id);

                product.id = data.id;
                product.name = data.name;
                product.price = priceFormat(data.price);
                product.merchant_id = data.merchant_id;

                page.cardTitle = 'Edit product';
            } catch (error) {
                console.error('Error loading state:', error);
            } finally {
                page.isLoading = false;
            }
        }

        function change() {
            page.isLoading = true;
            const data = { ...product };

            data.price = data.price.split('.').join("");
            if (route.name !== 'ProductForm') {
                store.dispatch('Product/update', { data })
                    .then(() => {
                        router.push({
                            name: 'Product',
                        });
                    })
                    .catch((err) => {
                        if (err.response && err.response.status === 403) {
                            error.value = err.response.data.message;
                        } else {
                            validation.value = JSON.parse(err.response.data.error);
                        }
                    })
                    .finally(() => {
                        page.isLoading = false;
                    });
            } else {
                store.dispatch('Product/create', { data })
                    .then(() => {
                        router.push({
                            name: 'Product',
                            query: { newProduct: 'true' }
                        });
                    })
                    .catch((err) => {
                        if (err.response && err.response.status === 403) {
                            error.value = err.response.data.message;
                        } else {
                            validation.value = JSON.parse(err.response.data.error);
                        }
                    })
                    .finally(() => {
                        page.isLoading = false;
                    });
            }
        }

        async function loadSelectedBusinessProfile() {
            await store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                selectedBusinessProfile.branch_id = businessProfile.branch_id;
                selectedBusinessProfile.merchant_id = businessProfile.merchant_id;

                if (businessProfile.merchant_id != '') {
                    product.merchant_id = businessProfile.merchant_id;
                } else {
                    loadMerchants();
                }
            });
        }

        function loadMerchants() {
            const filter = {
                branch_id: selectedBusinessProfile.branch_id,
            }

            store.commit('Merchant/add_filter', {filter});
            store.dispatch('Merchant/read').then(() => {
                merchants.value = [...store.state.Merchant.merchants.map((merch)=>({'value':merch.id,'text':merch.name}))]
            })
        }

        function priceFormat(subtotal) {
            const formated = subtotal.toString().replace(/[^0-9]/g,'').replace(/(\d)(?=(\d{3})+(?!\d))/g,'$1.');
            return formated;
        }
        
        function cancel() {
            router.push({ name: 'Product' });
        }

        return {
            product,
            validation,
            router,
            options,
            route,
            change,
            page,
            merchants,
            loadMerchants,
            priceFormat,
            selectedBusinessProfile,
            cancel,
            error,
            isMerchantProductEnabled
        }
    },
}
</script>

<style scoped>
/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}
</style>